import { api, ensureApiAuthenticated, getApiClient } from '../services/api';

export interface EmployeeSummary {
  id: string;
  employee_code: string;
  display_name: string;
  timezone: string;
  hourly_rate?: number | string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EmployeeLoginResponse {
  token: string;
  token_type: string;
  expires_in_seconds: number;
  employee: EmployeeSummary;
}

export interface EmployeeStatusResponse {
  employee_id: string;
  employee_code: string;
  display_name: string;
  currently_clocked_in: boolean;
  active_entry?: {
    entry_id: string;
    clock_in_at: string;
    elapsed_minutes: number;
  } | null;
}

export interface ClockInResponse {
  entry_id: string;
  employee_id: string;
  clock_in_at: string;
  status: string;
}

export interface ClockOutResponse {
  entry_id: string;
  employee_id: string;
  clock_in_at: string;
  clock_out_at: string;
  duration_minutes: number;
  duration_hours: number;
  status: string;
}

export interface WeeklyEmployeeHoursItem {
  employee_id: string;
  employee_code: string;
  display_name: string;
  total_minutes: number;
  total_hours: number;
  hourly_rate?: number | string | null;
  earned?: number | string | null;
  entries_count: number;
  open_entries_count: number;
}

export interface WeeklyReportResponse {
  week_start: string;
  week_end: string;
  timezone: string;
  employees: WeeklyEmployeeHoursItem[];
}

export interface EmployeeCreateRequest {
  employee_code: string;
  display_name: string;
  pin: string;
  timezone: string;
  hourly_rate?: number | null;
}

export interface EmployeeUpdateRequest {
  display_name?: string;
  timezone?: string;
  is_active?: boolean;
  hourly_rate?: number | null;
}

export interface EmployeeTimeEntry {
  id: string;
  employee_id: string;
  clock_in_at: string;
  clock_out_at?: string | null;
  source: string;
  note?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  duration_minutes?: number | null;
}

export interface EmployeeTimeEntryCreateRequest {
  clock_in_at: string;
  clock_out_at?: string;
  note?: string;
}

export interface EmployeeTimeEntryUpdateRequest {
  clock_in_at?: string;
  clock_out_at?: string | null;
  note?: string | null;
}

export interface EmployeePayment {
  id: string;
  employee_id: string;
  amount: number | string;
  paid_at: string;
  note?: string | null;
  created_by: string;
  created_at: string;
}

export interface EmployeePaymentCreateRequest {
  amount: number;
  paid_at?: string;
  note?: string;
}

export interface EmployeeBalanceItem {
  employee_id: string;
  employee_code: string;
  display_name: string;
  is_active: boolean;
  hourly_rate?: number | string | null;
  total_minutes: number;
  total_hours: number;
  earned: number | string;
  paid: number | string;
  owed: number | string;
}

export interface BalancesReportResponse {
  employees: EmployeeBalanceItem[];
}

const EMPLOYEE_TOKEN_STORAGE_KEY = 'tcg_employee_time_token';

export function getEmployeeTimeToken(): string | null {
  return sessionStorage.getItem(EMPLOYEE_TOKEN_STORAGE_KEY);
}

export function setEmployeeTimeToken(token: string): void {
  sessionStorage.setItem(EMPLOYEE_TOKEN_STORAGE_KEY, token);
}

export function clearEmployeeTimeToken(): void {
  sessionStorage.removeItem(EMPLOYEE_TOKEN_STORAGE_KEY);
}

function employeeHeaders(token: string) {
  return {
    'X-Employee-Token': token,
  };
}

export async function employeeLogin(employeeCode: string, pin: string): Promise<EmployeeLoginResponse> {
  const response = await getApiClient().post<EmployeeLoginResponse>('/time/employee/login', {
    employee_code: employeeCode,
    pin,
  });
  return response.data;
}

export async function employeeLogout(token: string): Promise<void> {
  await getApiClient().post('/time/employee/logout', {}, { headers: employeeHeaders(token) });
}

export async function fetchEmployeeStatus(token: string): Promise<EmployeeStatusResponse> {
  const response = await getApiClient().get<EmployeeStatusResponse>('/time/me/status', {
    headers: employeeHeaders(token),
  });
  return response.data;
}

export async function employeeClockIn(token: string, note?: string): Promise<ClockInResponse> {
  const response = await getApiClient().post<ClockInResponse>(
    '/time/me/clock-in',
    note ? { note } : {},
    { headers: employeeHeaders(token) },
  );
  return response.data;
}

export async function employeeClockOut(token: string, note?: string): Promise<ClockOutResponse> {
  const response = await getApiClient().post<ClockOutResponse>(
    '/time/me/clock-out',
    note ? { note } : {},
    { headers: employeeHeaders(token) },
  );
  return response.data;
}

export async function fetchWeeklyHoursReport(weekStart: string, timezone = 'America/Los_Angeles'): Promise<WeeklyReportResponse> {
  await ensureApiAuthenticated();
  const response = await api.get<WeeklyReportResponse>('/time/reports/weekly', {
    params: {
      week_start: weekStart,
      timezone,
    },
  });
  return response.data;
}

export async function downloadWeeklyHoursCsv(
  weekStart: string,
  timezone = 'America/Los_Angeles',
): Promise<void> {
  await ensureApiAuthenticated();
  const response = await api.get<Blob>('/time/reports/weekly.csv', {
    params: { week_start: weekStart, timezone },
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = `weekly-hours-${weekStart}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadPayBalancesCsv(): Promise<void> {
  await ensureApiAuthenticated();
  const response = await api.get<Blob>('/time/reports/balances.csv', {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'employee-balances.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function listEmployees(): Promise<EmployeeSummary[]> {
  await ensureApiAuthenticated();
  const response = await api.get<EmployeeSummary[]>('/time/employees');
  return response.data;
}

export async function createEmployee(payload: EmployeeCreateRequest): Promise<EmployeeSummary> {
  await ensureApiAuthenticated();
  const response = await api.post<EmployeeSummary>('/time/employees', payload);
  return response.data;
}

export async function updateEmployee(employeeId: string, payload: EmployeeUpdateRequest): Promise<EmployeeSummary> {
  await ensureApiAuthenticated();
  const response = await api.patch<EmployeeSummary>(`/time/employees/${employeeId}`, payload);
  return response.data;
}

export async function resetEmployeePin(employeeId: string, pin: string): Promise<void> {
  await ensureApiAuthenticated();
  await api.post(`/time/employees/${employeeId}/reset-pin`, { pin });
}

export async function listEmployeeEntries(employeeId: string): Promise<EmployeeTimeEntry[]> {
  await ensureApiAuthenticated();
  const response = await api.get<EmployeeTimeEntry[]>(`/time/employees/${employeeId}/entries`);
  return response.data;
}

export async function createEmployeeEntry(employeeId: string, payload: EmployeeTimeEntryCreateRequest): Promise<EmployeeTimeEntry> {
  await ensureApiAuthenticated();
  const response = await api.post<EmployeeTimeEntry>(`/time/employees/${employeeId}/entries`, payload);
  return response.data;
}

export async function updateEmployeeEntry(
  employeeId: string,
  entryId: string,
  payload: EmployeeTimeEntryUpdateRequest,
): Promise<EmployeeTimeEntry> {
  await ensureApiAuthenticated();
  const response = await api.patch<EmployeeTimeEntry>(`/time/employees/${employeeId}/entries/${entryId}`, payload);
  return response.data;
}

export async function deleteEmployeeEntry(employeeId: string, entryId: string): Promise<void> {
  await ensureApiAuthenticated();
  await api.delete(`/time/employees/${employeeId}/entries/${entryId}`);
}

export async function listEmployeePayments(employeeId: string): Promise<EmployeePayment[]> {
  await ensureApiAuthenticated();
  const response = await api.get<EmployeePayment[]>(`/time/employees/${employeeId}/payments`);
  return response.data;
}

export async function createEmployeePayment(
  employeeId: string,
  payload: EmployeePaymentCreateRequest,
): Promise<EmployeePayment> {
  await ensureApiAuthenticated();
  const response = await api.post<EmployeePayment>(`/time/employees/${employeeId}/payments`, payload);
  return response.data;
}

export async function deleteEmployeePayment(employeeId: string, paymentId: string): Promise<void> {
  await ensureApiAuthenticated();
  await api.delete(`/time/employees/${employeeId}/payments/${paymentId}`);
}

export async function fetchPayBalances(): Promise<BalancesReportResponse> {
  await ensureApiAuthenticated();
  const response = await api.get<BalancesReportResponse>('/time/reports/balances');
  return response.data;
}